import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";

// Thin wrapper around useSearchParams for admin list pages (UserList,
// OrderList, ShopList, ProductList, ReportList, FeedbackList,
// VerificationList, DisputeList): pagination and filter state live in the
// URL instead of local useState, so clicking into a detail row and then
// hitting the browser back button restores the exact page/filters the list
// was showing, rather than resetting to page 0.
//
// updateParams(updates) merges `updates` into the current query string using
// `replace: true` — adjusting a filter/page updates the list's existing
// history entry instead of pushing a new one, so only an actual navigation
// (e.g. clicking a row) adds a back-button stop. A value of "", undefined,
// or null removes that param rather than writing it, keeping the URL clean
// for the default/unfiltered, page-1 state.
const useListQueryState = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const updateParams = useCallback(
    (updates) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [key, value] of Object.entries(updates)) {
            if (value === "" || value === undefined || value === null) {
              next.delete(key);
            } else {
              next.set(key, String(value));
            }
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  return [searchParams, updateParams];
};

export default useListQueryState;
